import type { CompanySettings } from './types';

import {
  type QueryFunctionContext,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCompany } from '../queryKeys';

import { fetchCompanySettings } from './fetchCompanySettings';

/**
 * Generates the query options for the company settings query.
 *
 * @param companyId - The ID of the company to fetch settings for.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getCompanySettingsQueryOptions = <TData>({
  companyId,
  options,
}: {
  companyId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<CompanySettings, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) => ({
  enabled: Boolean(companyId),
  queryFn: async ({ signal }: QueryFunctionContext) =>
    fetchCompanySettings(companyId!, { signal }),
  queryKey: queryKeysCompany.getSettings(companyId ?? ''),
  staleTime: ms('60s'),
  ...options,
});

/**
 * React Query based custom hook for getting the settings for a company with a given companyId.
 * Requires ADMIN access.
 *
 * @param companyId - The ID of the company to fetch settings for.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_company_vadmin_settings_vadmin_company__company_id__vadmin_settings_get
 */
export const useQueryCompanySettings = (
  companyId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<CompanySettings, Error, CompanySettings>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<CompanySettings, Error, CompanySettings>(
    getCompanySettingsQueryOptions<CompanySettings>({
      companyId,
      options,
    }),
  );
